import { useEffect, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  PaperPlaneRightIcon,
  TrashIcon,
  ArrowClockwiseIcon,
  WifiSlashIcon,
  ArrowLeftIcon,
  SparkleIcon,
  XIcon,
} from "@phosphor-icons/react";
import { useI18N } from "common/hooks";
import {
  useVenueAssistant,
  VENUE_ASSISTANT_STARTER_QUESTIONS,
} from "common/aiengine";
import { cn } from "@/lib/utils";

type VenueAssistantTabProps = {
  venueSlug: string;
  venueName: string;
  /** When provided, renders a close button in the header (← on mobile, × on desktop). */
  onClose?: () => void;
  /** "back" renders an ArrowLeft on the left; "x" renders an X on the right. Default "x". */
  closeVariant?: "back" | "x";
};

/** Markdown renderer styled to the venue-details theme. Assistant answers come
 *  back as markdown (bold, lists, links, inline images). */
function AssistantMarkdown({ content }: { content: string }) {
  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      components={{
        p: ({ children }) => (
          <p className="label-m-medium text-fg-neutral-strong mb-2 last:mb-0">
            {children}
          </p>
        ),
        // Area headings (### Area) become tile-style section headers so
        // multi-area answers read as distinct, scannable blocks.
        h3: ({ children }) => (
          <h3 className="label-m-semibold text-fg-neutral-strong mt-3 first:mt-0 mb-1.5 px-3 py-2 rounded-lg bg-container-brand-minimal">
            {children}
          </h3>
        ),
        h4: ({ children }) => (
          <h4 className="label-s-semibold text-fg-neutral-strong mt-2 mb-1">
            {children}
          </h4>
        ),
        hr: () => (
          <hr className="my-3 border-t border-border-token-brand-minimal" />
        ),
        a: ({ children, href }) => (
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="label-m-semibold text-fg-brand-subtle underline hover:text-fg-brand-strong"
          >
            {children}
          </a>
        ),
        ul: ({ children }) => (
          <ul className="list-disc pl-5 my-2 flex flex-col gap-1">{children}</ul>
        ),
        ol: ({ children }) => (
          <ol className="list-decimal pl-5 my-2 flex flex-col gap-1">
            {children}
          </ol>
        ),
        li: ({ children }) => (
          <li className="label-m-medium text-fg-neutral-strong">{children}</li>
        ),
        strong: ({ children }) => (
          <strong className="label-m-semibold text-fg-neutral-strong">
            {children}
          </strong>
        ),
        img: ({ src, alt }) => (
          <img
            src={typeof src === "string" ? src : ""}
            alt={alt || ""}
            loading="lazy"
            className="my-2 max-h-[260px] w-auto max-w-full rounded-lg object-cover"
          />
        ),
      }}
    >
      {content}
    </ReactMarkdown>
  );
}

function VenueAssistantTab({
  venueSlug,
  venueName,
  onClose,
  closeVariant = "x",
}: VenueAssistantTabProps) {
  const i18n = useI18N();
  const { messages, isLoading, error, suggestions, send, retry, clear } =
    useVenueAssistant(venueSlug);

  const [input, setInput] = useState("");
  const [isOnline, setIsOnline] = useState(
    typeof navigator === "undefined" ? true : navigator.onLine,
  );

  const scrollRef = useRef<HTMLDivElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Track connectivity for the offline state.
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  // On mount / returning to tab: jump to the bottom instantly (no animation).
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ block: "end" });
  }, []);

  // Auto-scroll to the latest message smoothly whenever new content arrives.
  useEffect(() => {
    if (messages.length === 0 && !isLoading && !error) return;
    bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages, isLoading, error, suggestions]);

  const isEmpty = messages.length === 0;
  const canSend = !!input.trim() && !isLoading && isOnline;

  const handleSend = () => {
    if (!canSend) {
      return;
    }
    send(input);
    setInput("");
    inputRef.current?.focus();
  };

  const handleChipClick = (question: string) => {
    if (isLoading || !isOnline) {
      return;
    }
    send(question);
    inputRef.current?.focus();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="flex flex-col h-full min-h-[420px] w-full">
      {/* Header */}
      <div
        className={cn(
          "flex items-center gap-2 pb-3 border-b border-border-token-brand-minimal shrink-0",
          onClose ? "pt-0" : "pt-3 md:pt-0",
        )}
      >
        {/* Back button — mobile overlay variant */}
        {onClose && closeVariant === "back" && (
          <button
            onClick={onClose}
            className="flex items-center justify-center w-9 h-9 -ml-1 shrink-0 hover:bg-bg-brand-ghost-hover rounded-lg transition-colors"
            aria-label="Back"
          >
            <ArrowLeftIcon size={20} className="text-fg-neutral-strong" />
          </button>
        )}

        {/* Title */}
        <div className="flex-1 flex items-center gap-2 min-w-0">
          <SparkleIcon
            size={18}
            weight="fill"
            aria-hidden="true"
            className="text-fg-brand-strong shrink-0"
          />
          <div className="min-w-0">
            <h2 className="label-m-semibold text-fg-neutral-strong">
              {i18n.get("askAi")}
            </h2>
            <p className="label-s-medium text-fg-neutral-subtle line-clamp-1">
              {venueName}
            </p>
          </div>
        </div>

        {/* Clear chat */}
        {!isEmpty && (
          <button
            onClick={clear}
            className="border border-border-token-brand-minimal rounded-lg px-4 py-2.5 flex gap-2 items-center hover:bg-bg-brand-ghost-hover transition-colors shrink-0 text-fg-neutral-strong"
            aria-label={i18n.get("clearChat")}
          >
            <TrashIcon size={20} className="text-fg-neutral-strong" aria-hidden="true" />
            <span className="label-m-medium">{i18n.get("clearChat")}</span>
          </button>
        )}

        {/* X close button — desktop panel variant */}
        {onClose && closeVariant === "x" && (
          <button
            onClick={onClose}
            className="flex items-center justify-center w-9 h-9 shrink-0 hover:bg-bg-brand-ghost-hover rounded-lg transition-colors"
            aria-label="Close"
          >
            <XIcon size={20} className="text-fg-neutral-strong" />
          </button>
        )}
      </div>

      {/* Messages / Empty state */}
      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto flex flex-col gap-3 py-4 scrollbar-thin"
        role="log"
        aria-live="polite"
        aria-label={i18n.get("askAiAboutThisVenue")}
      >
        {isEmpty && (
          <div className="flex flex-col gap-4">
            <div className="self-start max-w-[85%] px-4 py-3 rounded-2xl rounded-bl-md bg-bg-brand-ghost-default border border-border-token-brand-minimal">
              <p className="label-m-medium text-fg-neutral-strong">
                {i18n.get("askAiWelcome")}
              </p>
            </div>

            {/* Starter question chips */}
            <div className="flex flex-wrap gap-2">
              {VENUE_ASSISTANT_STARTER_QUESTIONS.map((question) => {
                const label = i18n.get(question.i18nKey);
                return (
                  <button
                    key={question.id}
                    onClick={() => handleChipClick(label)}
                    disabled={isLoading || !isOnline}
                    className="px-3 py-2 rounded-full border border-border-token-brand-minimal label-s-medium text-fg-neutral-strong hover:bg-bg-brand-ghost-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed min-h-[36px]"
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {messages.map((message) => (
          <div
            key={message.id}
            className={cn(
              "flex",
              message.role === "user" ? "justify-end" : "justify-start",
            )}
          >
            <div
              className={cn(
                "max-w-[85%] px-4 py-3 rounded-2xl break-words",
                message.role === "user"
                  ? "bg-bg-brand-primary-default text-fg-neutral-onFilled rounded-br-md"
                  : "bg-bg-brand-ghost-default border border-border-token-brand-minimal rounded-bl-md",
              )}
            >
              {message.role === "user" ? (
                <p className="label-m-medium text-fg-neutral-onFilled whitespace-pre-wrap break-words">
                  {message.content}
                </p>
              ) : (
                <AssistantMarkdown content={message.content} />
              )}
            </div>
          </div>
        ))}

        {/* Guided-chat follow-up chips — tied to the latest answer */}
        {!isEmpty && !isLoading && !error && suggestions.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {suggestions.map((question, index) => (
              <button
                key={`${index}-${question}`}
                onClick={() => handleChipClick(question)}
                disabled={isLoading || !isOnline}
                className="px-3 py-2 rounded-full border border-border-token-brand-minimal label-s-medium text-fg-neutral-strong hover:bg-bg-brand-ghost-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed min-h-[36px]"
              >
                {question}
              </button>
            ))}
          </div>
        )}

        {/* Thinking indicator */}
        {isLoading && (
          <div className="flex justify-start">
            <div className="bg-bg-brand-ghost-default border border-border-token-brand-minimal rounded-2xl rounded-bl-md px-4 py-3">
              <div className="flex items-center gap-1" aria-label="Loading">
                <span className="w-2 h-2 rounded-full bg-fg-brand-subtle animate-bounce [animation-delay:-0.32s]" />
                <span className="w-2 h-2 rounded-full bg-fg-brand-subtle animate-bounce [animation-delay:-0.16s]" />
                <span className="w-2 h-2 rounded-full bg-fg-brand-subtle animate-bounce" />
              </div>
            </div>
          </div>
        )}

        {/* Error state with retry */}
        {error && !isLoading && (
          <div className="flex justify-start">
            <div className="max-w-[85%] flex flex-col gap-2 px-4 py-3 rounded-2xl rounded-bl-md bg-bg-error-tertiary-default border border-border-token-error-minimal">
              <p className="label-m-medium text-fg-error-subtle">
                {i18n.get(
                  error === "unavailable" ? "askAiUnavailable" : "askAiError",
                )}
              </p>
              <button
                onClick={retry}
                className="self-start border border-border-token-brand-minimal rounded-lg px-4 py-2.5 flex gap-2 items-center hover:bg-bg-brand-ghost-hover transition-colors text-fg-neutral-strong"
              >
                <ArrowClockwiseIcon size={20} className="text-fg-neutral-strong" aria-hidden="true" />
                <span className="label-m-medium">{i18n.get("askAiRetry")}</span>
              </button>
            </div>
          </div>
        )}

        {/* Scroll anchor — scrollIntoView targets this so auto-scroll works on
            both the constrained desktop inner div and the full-page mobile scroll. */}
        <div ref={bottomRef} aria-hidden="true" />
      </div>

      {/* Offline banner */}
      {!isOnline && (
        <div className="shrink-0 flex items-center gap-2 px-3 py-2 mb-2 rounded-lg bg-bg-error-tertiary-default border border-border-token-error-minimal">
          <WifiSlashIcon
            size={16}
            aria-hidden="true"
            className="text-fg-error-subtle shrink-0"
          />
          <p className="label-s-medium text-fg-error-subtle">
            {i18n.get("askAiOffline")}
          </p>
        </div>
      )}

      {/* Composer */}
      <div className="shrink-0 pt-3 border-t border-border-token-brand-minimal flex flex-col gap-2">
        <div className="flex items-center gap-2">
          <label htmlFor="venue-assistant-input" className="sr-only">
            {i18n.get("askAiPlaceholder")}
          </label>
          <input
            id="venue-assistant-input"
            ref={inputRef}
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={i18n.get("askAiPlaceholder")}
            autoComplete="off"
            disabled={!isOnline}
            className="flex-1 min-w-0 h-11 px-4 rounded-full border border-border-token-brand-minimal bg-bg-brand-secondary-default label-m-medium text-fg-neutral-strong placeholder:text-fg-neutral-subtle outline-none focus-blue disabled:opacity-50"
          />
          <button
            onClick={handleSend}
            disabled={!canSend}
            aria-label={i18n.get("send")}
            className="flex items-center justify-center w-11 h-11 shrink-0 rounded-full bg-bg-brand-primary-default hover:bg-bg-brand-primary-hover active:bg-bg-brand-primary-active transition-colors disabled:opacity-50 disabled:cursor-not-allowed focus-blue"
          >
            <PaperPlaneRightIcon
              size={20}
              weight="fill"
              aria-hidden="true"
              className="text-fg-neutral-onFilled"
            />
          </button>
        </div>
        <p className="label-s-regular text-fg-neutral-subtle text-center">
          {i18n.get("askAiDisclaimer")}
        </p>
      </div>
    </div>
  );
}

export default VenueAssistantTab;
